import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Modal,
  Form,
  Select,
  DatePicker,
  Input,
  Switch,
  Space,
  Alert,
  Typography,
  Row,
  Col,
  Divider,
  Collapse
} from 'antd';
import {
  MunicipalityAccessLevel,
  MunicipalityAssignment,
  MunicipalityPermissions,
  getPermissionPreset,
  getAccessLevelLabel,
  validatePermissions
} from '@municipality/types';
import dayjs from 'dayjs';

const { Option } = Select;
const { TextArea } = Input;
const { Panel } = Collapse;
const { Text } = Typography;

interface MunicipalityAccessModalProps {
  visible: boolean;
  assignment: (MunicipalityAssignment & { municipalityName?: string }) | null;
  onCancel: () => void;
  onSave: (
    municipalityId: string,
    updates: {
      accessLevel?: MunicipalityAccessLevel;
      permissions?: MunicipalityPermissions;
      expiresAt?: Date | null;
      notes?: string;
    }
  ) => Promise<void>;
  loading?: boolean;
}

const MunicipalityAccessModal: React.FC<MunicipalityAccessModalProps> = ({
  visible,
  assignment,
  onCancel,
  onSave,
  loading = false
}) => {
  const { t } = useTranslation('users');
  const [form] = Form.useForm();
  const [permissions, setPermissions] = useState<MunicipalityPermissions>(
    assignment?.permissions || getPermissionPreset(MunicipalityAccessLevel.CONTENT_MANAGER)
  );
  const [validationWarnings, setValidationWarnings] = useState<string[]>([]);

  useEffect(() => {
    if (assignment && visible) {
      form.setFieldsValue({
        accessLevel: assignment.accessLevel,
        expiresAt: assignment.expiresAt ? dayjs(assignment.expiresAt) : null,
        notes: assignment.notes || '',
      });
      setPermissions(assignment.permissions);
      
      // Validate initial permissions
      const validation = validatePermissions(assignment.permissions);
      setValidationWarnings(validation.warnings);
    }
  }, [assignment, visible, form]);

  const handleAccessLevelChange = (value: MunicipalityAccessLevel) => {
    const preset = getPermissionPreset(value);
    setPermissions(preset);
    
    // Validate permissions
    const validation = validatePermissions(preset);
    setValidationWarnings(validation.warnings);
  };

  const handlePermissionChange = (key: keyof MunicipalityPermissions, checked: boolean) => {
    const updatedPermissions = { ...permissions, [key]: checked };
    setPermissions(updatedPermissions);
    
    // Validate permissions
    const validation = validatePermissions(updatedPermissions);
    setValidationWarnings(validation.warnings);
  };

  const handleSubmit = async () => {
    if (!assignment) return;

    try {
      const values = await form.validateFields();
      await onSave(assignment.municipalityId, {
        accessLevel: values.accessLevel,
        permissions,
        expiresAt: values.expiresAt ? values.expiresAt.toDate() : null,
        notes: values.notes,
      });
      
      form.resetFields();
      setValidationWarnings([]);
    } catch (error) {
      console.error('Error updating assignment:', error);
    }
  };

  const accessLevelOptions = [
    {
      value: MunicipalityAccessLevel.READ_ONLY,
      label: getAccessLevelLabel(MunicipalityAccessLevel.READ_ONLY),
      description: t('accessLevels.readOnlyDescription', 'View-only access to analytics'),
    },
    {
      value: MunicipalityAccessLevel.CONTENT_MANAGER,
      label: getAccessLevelLabel(MunicipalityAccessLevel.CONTENT_MANAGER),
      description: t('accessLevels.contentManagerDescription', 'Manage content, surveys, events (no chat)'),
    },
    {
      value: MunicipalityAccessLevel.FULL_ACCESS,
      label: getAccessLevelLabel(MunicipalityAccessLevel.FULL_ACCESS),
      description: t('accessLevels.fullAccessDescription', 'Full access including chat and settings'),
    },
  ];

  const permissionsList: { key: keyof MunicipalityPermissions; label: string; category: string }[] = [
    // Content Management
    { key: 'canEditNews', label: t('permissions.canEditNews', 'Edit news'), category: 'content' },
    { key: 'canManageEvents', label: t('permissions.canManageEvents', 'Manage events'), category: 'content' },
    { key: 'canManageSurveys', label: t('permissions.canManageSurveys', 'Manage surveys'), category: 'content' },
    { key: 'canManagePOIs', label: t('permissions.canManagePOIs', 'Manage points of interest'), category: 'content' },
    { key: 'canManageVolunteering', label: t('permissions.canManageVolunteering', 'Manage volunteering'), category: 'content' },
    { key: 'canManageLostAndFound', label: t('permissions.canManageLostAndFound', 'Manage lost and found'), category: 'content' },
    { key: 'canManageIssues', label: t('permissions.canManageIssues', 'Manage issues'), category: 'content' },
    
    // Governance
    { key: 'canManageCouncil', label: t('permissions.canManageCouncil', 'Manage council'), category: 'governance' },
    { key: 'canManageMayor', label: t('permissions.canManageMayor', 'Manage mayor'), category: 'governance' },
    { key: 'canManageCommittees', label: t('permissions.canManageCommittees', 'Manage committees'), category: 'governance' },
    
    // Operations
    { key: 'canAccessChat', label: t('permissions.canAccessChat', 'Access chat'), category: 'operations' },
    { key: 'canManageTasks', label: t('permissions.canManageTasks', 'Manage tasks'), category: 'operations' },
    { key: 'canManageSocialMedia', label: t('permissions.canManageSocialMedia', 'Manage social media'), category: 'operations' },
    { key: 'canManageOnlinePayments', label: t('permissions.canManageOnlinePayments', 'Manage online payments'), category: 'operations' },
    { key: 'canManagePushNotifications', label: t('permissions.canManagePushNotifications', 'Manage push notifications'), category: 'operations' },
    
    // Administration
    { key: 'canManageUsers', label: t('permissions.canManageUsers', 'Manage users'), category: 'administration' },
    { key: 'canEditMunicipalitySettings', label: t('permissions.canEditMunicipalitySettings', 'Edit municipality settings'), category: 'administration' },
    
    // Analytics
    { key: 'canViewAnalytics', label: t('permissions.canViewAnalytics', 'View analytics'), category: 'analytics' },
  ];

  const permissionsByCategory = {
    content: permissionsList.filter(p => p.category === 'content'),
    governance: permissionsList.filter(p => p.category === 'governance'),
    operations: permissionsList.filter(p => p.category === 'operations'),
    administration: permissionsList.filter(p => p.category === 'administration'),
    analytics: permissionsList.filter(p => p.category === 'analytics'),
  };

  return (
    <Modal
      title={t('editMunicipalityAccess', 'Edit municipality access')}
      open={visible}
      onCancel={onCancel}
      onOk={handleSubmit}
      confirmLoading={loading}
      width={700}
      okText={t('save', 'Save')}
      cancelText={t('cancel', 'Cancel')}
    >
      {assignment && (
        <>
          <Alert
            message={assignment.municipalityName || assignment.municipalityId}
            type="info"
            showIcon
            style={{ marginBottom: 16 }}
          />

          <Form
            form={form}
            layout="vertical"
            initialValues={{
              accessLevel: assignment.accessLevel,
              expiresAt: assignment.expiresAt ? dayjs(assignment.expiresAt) : null,
              notes: assignment.notes || '',
            }}
          >
            <Form.Item
              name="accessLevel"
              label={t('accessLevel', 'Access level')}
              rules={[{ required: true }]}
            >
              <Select
                placeholder={t('selectAccessLevel', 'Select access level')}
                onChange={handleAccessLevelChange}
                optionLabelProp="label"
              >
                {accessLevelOptions.map((option) => (
                  <Option key={option.value} value={option.value} label={option.label}>
                    <div>
                      <div style={{ fontWeight: 500 }}>{option.label}</div>
                      <div style={{ fontSize: 12, color: '#8c8c8c' }}>{option.description}</div>
                    </div>
                  </Option>
                ))}
              </Select>
            </Form.Item>

            {validationWarnings.length > 0 && (
              <Alert
                message={t('permissionWarnings', 'Permission warnings')}
                description={
                  <ul style={{ margin: 0, paddingLeft: 20 }}>
                    {validationWarnings.map((warning, index) => (
                      <li key={index}>{warning}</li>
                    ))}
                  </ul>
                }
                type="warning"
                showIcon
                style={{ marginBottom: 16 }}
              />
            )}

            <Collapse ghost>
              <Panel header={t('customizePermissions', 'Customize permissions')} key="permissions">
                <Space direction="vertical" style={{ width: '100%' }} size="small">
                  {Object.entries(permissionsByCategory).map(([category, perms]) => (
                    <div key={category}>
                      <Text strong style={{ textTransform: 'capitalize' }}>
                        {t(`permissionCategories.${category}`, category)}
                      </Text>
                      <Divider style={{ margin: '8px 0' }} />
                      <Row gutter={[16, 8]}>
                        {perms.map((perm) => (
                          <Col span={12} key={perm.key}>
                            <Space>
                              <Switch
                                checked={permissions[perm.key]}
                                onChange={(checked) => handlePermissionChange(perm.key, checked)}
                                size="small"
                              />
                              <Text style={{ fontSize: 12 }}>{perm.label}</Text>
                            </Space>
                          </Col>
                        ))}
                      </Row>
                    </div>
                  ))}
                </Space>
              </Panel>
            </Collapse>

            <Form.Item
              name="expiresAt"
              label={t('expirationDate', 'Expiration date (optional)')}
              extra={t('leaveBlankForNoExpiration', 'Leave blank for no expiration')}
            >
              <DatePicker style={{ width: '100%' }} />
            </Form.Item>

            <Form.Item
              name="notes"
              label={t('notes', 'Notes (optional)')}
            >
              <TextArea rows={3} placeholder={t('addNotes', 'Add notes...')} />
            </Form.Item>
          </Form>
        </>
      )}
    </Modal>
  );
};

export default MunicipalityAccessModal;
